import { FormEvent, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { formatLocalTime } from "../utilis/methods/localDateTime";
import { colors } from "../config";
import { getConstants } from "@utilifiles/methods/storage/storage";
import { useMessenger } from "./MessengerContext";
import type { UiChatMessage } from "./types";

const tickBase = { fontSize: 14, ml: 0.5 } as const;

function ReceiptTicks({ message }: { message: UiChatMessage }) {
  const status = message.deliveryStatus;

  // Not delivered / sent: single white tick
  if (!status || status === "PENDING" || status === "SENT") {
    return (
      <DoneIcon
        sx={{
          ...tickBase,
          color: "#ffffff",
          opacity: status === "SENT" ? 1 : 0.8,
        }}
      />
    );
  }

  // Delivered: double white tick, no glow
  if (status === "DELIVERED") {
    return (
      <DoneAllIcon
        sx={{
          ...tickBase,
          color: "#ffffff",
        }}
      />
    );
  }

  // Seen/read: double green tick
  return (
    <DoneAllIcon
      aria-label="Seen"
      sx={{
        ...tickBase,
        color: colors.success200,
      }}
    />
  );
}

export default function ChatThread({
  onBack,
  showBack,
}: {
  onBack?: () => void;
  showBack?: boolean;
}) {
  const theme = useTheme();
  const {
    activeConversation,
    messages,
    messagesLoading,
    sendMessage,
  } = useMessenger();
  const { userId } = getConstants();
  const currentUserId = Number(userId || 0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeConversation?.id]);

  if (!activeConversation) {
    return (
      <Box
        className="flex flex-col items-center justify-center h-full p-6 text-center gap-2"
        sx={{ bgcolor: "background.default" }}
      >
        <Typography variant="subtitle1" color="text.primary">
          Select a conversation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose a chat from the list or start a new message
        </Typography>
      </Box>
    );
  }

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await sendMessage(text);
      setText("");
    } finally {
      setSending(false);
    }
  };

  return (
    <Box className="flex flex-col h-full">
      <Box
        className="flex items-center gap-2 px-2 py-2 border-b"
        sx={{
          borderColor: "divider",
          bgcolor: "background.paper",
          color: "text.primary",
        }}
      >
        {showBack && (
          <IconButton
            size="small"
            onClick={onBack}
            aria-label="Back"
            color="primary"
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Avatar
          src={activeConversation.peerProfileImage || undefined}
          sx={{
            width: 36,
            height: 36,
            bgcolor: colors.primaryMain,
          }}
        >
          {(activeConversation.peerDisplayName || "?").charAt(0).toUpperCase()}
        </Avatar>
        <Box className="flex flex-col min-w-0 flex-1">
          <Typography variant="subtitle2" noWrap color="text.primary">
            {activeConversation.peerDisplayName ||
              `User ${activeConversation.peerUserId}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            
          </Typography>
        </Box>
      </Box>

      <Box
        className="flex-1 overflow-y-auto px-3 py-2"
        sx={{ bgcolor: "background.default" }}
      >
        {messagesLoading ? (
          <Box className="flex justify-center py-8">
            <CircularProgress size={28} color="primary" />
          </Box>
        ) : (
          messages.map((msg) => {
            const mine = msg.senderId === currentUserId;
            return (
              <Box
                key={msg.clientId || msg.messageId}
                className={`flex mb-2 ${mine ? "justify-end" : "justify-start"}`}
              >
                <Box
                  sx={{
                    maxWidth: "78%",
                    px: 1.5,
                    py: 1.15,
                    borderRadius: mine ? "18px 18px 6px 18px" : "18px 18px 18px 6px",
                    color: mine ? "#ffffff" : "text.primary",
                    background: mine
                      ? `linear-gradient(135deg, ${colors.primaryMain} 0%, ${colors.primaryDark} 100%)`
                      : theme.palette.background.paper,
                    border: mine ? "none" : "1px solid",
                    borderColor: colors.grey300,
                    boxShadow: mine
                      ? "0 8px 20px rgba(33,150,243,0.22)"
                      : "0 4px 12px rgba(15,23,42,0.06)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      lineHeight: 1.45,
                    }}
                  >
                    {msg.message}
                  </Typography>
                  <Box className="flex items-center justify-end mt-0.5">
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.8,
                        fontSize: 10,
                        color: mine ? "#ffffff" : "text.secondary",
                      }}
                    >
                      {formatLocalTime(msg.createdAt, "HH:mm", "")}
                    </Typography>
                    {mine && <ReceiptTicks message={msg} />}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
        <div ref={bottomRef} />
      </Box>

      <Box
        component="form"
        onSubmit={(e) => void handleSubmit(e)}
        className="flex items-end gap-1 p-2 border-t"
        sx={{
          borderColor: "divider",
          bgcolor: colors.grey100,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          maxRows={4}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSubmit();
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 6,
              bgcolor: "background.paper",
            },
          }}
        />
        <IconButton
          color="primary"
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
            "&.Mui-disabled": {
              bgcolor: colors.grey300,
              color: "background.paper",
            },
          }}
          type="submit"
          disabled={!text.trim() || sending}
          aria-label="Send"
        >
          {sending ? (
            <CircularProgress size={20} sx={{ color: "inherit" }} />
          ) : (
            <SendIcon />
          )}
        </IconButton>
      </Box>
    </Box>
  );
}
